<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// SiteUp (Cartões de Apoio): o miolo do painel do chip. Só apresentação — quem busca,
// recarrega e trata falha é o AssistDataChip. Um renderizador por tipo de dado, porque
// imóvel, pedido e acesso não se leem do mesmo jeito.
const props = defineProps({
  kind: {
    type: String,
    default: '',
  },
  items: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

// A url do imóvel vem do radar, que agrega ~47 fontes de fora da casa: um `javascript:`
// vindo de lá viraria clique executável no painel de todo atendente. Mesma disciplina do
// safe_href do servidor: sem esquema conhecido o item continua na lista, só não é link.
const SAFE_PROTOCOLS = ['http:', 'https:'];

const safeHref = href => {
  if (typeof href !== 'string' || !href.trim()) return null;
  try {
    const { protocol } = new URL(href, window.location.origin);
    return SAFE_PROTOCOLS.includes(protocol) ? href : null;
  } catch {
    return null;
  }
};

const properties = computed(() =>
  props.items.map((item, index) => ({
    ...item,
    key: item.codigo || index,
    href: safeHref(item.url),
  }))
);

const formatBRL = value => {
  if (value === null || value === undefined || value === '') {
    return t('CONVERSATION.ASSIST_CHIPS.PROPERTY.NO_PRICE');
  }
  return `R$ ${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};
</script>

<template>
  <div v-if="kind === 'property_list'" class="flex flex-col gap-2">
    <component
      :is="item.href ? 'a' : 'div'"
      v-for="item in properties"
      :key="item.key"
      :href="item.href"
      :target="item.href ? '_blank' : null"
      :rel="item.href ? 'noopener noreferrer' : null"
      class="flex gap-2 rounded-xl border border-n-weak p-2"
      :class="item.href ? 'hover:bg-n-alpha-1' : ''"
    >
      <span
        class="flex size-14 flex-none items-center justify-center overflow-hidden rounded-lg bg-n-alpha-2"
      >
        <img
          v-if="item.foto"
          :src="item.foto"
          :alt="item.titulo"
          class="size-full object-cover"
        />
        <span v-else class="i-ri-image-line size-5 text-n-slate-10" />
      </span>
      <span class="flex min-w-0 flex-1 flex-col gap-0.5">
        <span class="flex items-start justify-between gap-2">
          <span class="line-clamp-2 text-sm font-medium text-n-slate-12">
            {{ item.titulo }}
          </span>
          <span class="whitespace-nowrap text-sm font-medium text-n-teal-11">
            {{ formatBRL(item.valor) }}
          </span>
        </span>
        <span class="truncate text-xs text-n-slate-11">{{ item.bairro }}</span>
        <!-- Selo da imobiliária: item do radar é de concorrente, e o atendente
             precisa saber disso ANTES de mandar o link pro cliente. -->
        <span
          v-if="item.fonte"
          class="w-fit rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-11"
        >
          {{ item.fonte }}
        </span>
      </span>
    </component>
  </div>

  <div v-else-if="kind === 'orders'" class="flex flex-col gap-2">
    <!-- Com url (admin da Shopify), o pedido inteiro vira link em aba nova:
         sem isso o atendente lia o numero e cacava o pedido na mao. Mesma
         sanitizacao dos imoveis: esquema desconhecido continua item, sem link. -->
    <component
      :is="safeHref(item.url) ? 'a' : 'div'"
      v-for="(item, index) in items"
      :key="item.numero || index"
      :href="safeHref(item.url) || undefined"
      :target="safeHref(item.url) ? '_blank' : undefined"
      :rel="safeHref(item.url) ? 'noopener noreferrer' : undefined"
      class="flex flex-col gap-0.5 rounded-xl border border-n-weak p-2"
      :class="safeHref(item.url) ? 'hover:bg-n-alpha-2' : ''"
    >
      <div class="flex items-center justify-between gap-2">
        <span
          class="truncate text-sm font-medium text-n-slate-12"
          :class="
            safeHref(item.url)
              ? 'underline decoration-n-weak underline-offset-2'
              : ''
          "
        >
          {{ item.numero }}
        </span>
        <span class="whitespace-nowrap text-sm font-medium text-n-teal-11">
          {{ item.total }}
        </span>
      </div>
      <span class="truncate text-xs text-n-slate-11">{{ item.status }}</span>
      <span v-if="item.rastreio" class="truncate text-xs text-n-slate-11">
        {{
          t('CONVERSATION.ASSIST_CHIPS.ORDERS.TRACKING', {
            value: item.rastreio,
          })
        }}
      </span>
    </component>
  </div>

  <div v-else class="flex flex-col gap-2">
    <div
      v-for="(item, index) in items"
      :key="item.produto || index"
      class="flex flex-col gap-0.5 rounded-xl border border-n-weak p-2"
    >
      <span class="truncate text-sm font-medium text-n-slate-12">
        {{ item.produto }}
      </span>
      <span class="truncate text-xs text-n-slate-11">{{ item.status }}</span>
      <span v-if="item.validade" class="truncate text-xs text-n-slate-11">
        {{
          t('CONVERSATION.ASSIST_CHIPS.ACCESS.VALID_UNTIL', {
            value: item.validade,
          })
        }}
      </span>
    </div>
  </div>
</template>
